import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, CheckCircle2, ChevronLeft, ChevronUp, X, User, Mail, Phone, MessageSquare } from "lucide-react";
import { cloudinaryThumb, dkk } from "../../lib/publicUtils";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const KM_PACKAGES = [
  { km: 1500, addon: 0,    label: "1500 km", note: "inkluderet" },
  { km: 2000, addon: 500,  label: "2000 km", note: "500,00 kr." },
  { km: 2500, addon: 1000, label: "2500 km", note: "1.000,00 kr." },
  { km: 3000, addon: 1500, label: "3000 km", note: "1.500,00 kr." },
];

const INSURANCE_PACKAGES = [
  {
    id: "basic",
    title: "Standard",
    deductible: 15000,
    addon: 0,
    badge: null,
    addonLabel: "Inkluderet",
  },
  {
    id: "extra",
    title: "Reduceret selvrisiko",
    deductible: 5000,
    addon: 500,
    badge: { text: "ANBEFALET", color: "emerald" },
    addonLabel: "500 kr. pr. måned",
  },
  {
    id: "full",
    title: "Nul selvrisiko",
    deductible: 0,
    addon: 900,
    badge: { text: "BEKYMRINGSFRI", color: "blue" },
    addonLabel: "900 kr. pr. måned",
  },
];

function addMonths(dateStr, months) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split("T")[0];
}
function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

export default function BookCar() {
  const { plate } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialBinding = parseInt(searchParams.get("binding") || "", 10) || null;
  const [car, setCar] = useState(null);
  const [siteInfo, setSiteInfo] = useState({});
  const [loading, setLoading] = useState(true);

  // Form state
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [bindingMonths, setBindingMonths] = useState(null);
  const [kmPackage, setKmPackage] = useState(KM_PACKAGES[0]);
  const [insurance, setInsurance] = useState(INSURANCE_PACKAGES[0]); // Standard 15.000 kr selvrisiko som default
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/cars?type=rent`),
      axios.get(`${API}/public/site-info`),
    ])
      .then(([carsRes, siRes]) => {
        const match = (carsRes.data?.cars || []).find((c) => c.license_plate === plate);
        setCar(match);
        setSiteInfo(siRes.data || {});
        if (match) {
          const opts = [
            { months: 1, price: match.sub_price_1m },
            { months: 3, price: match.sub_price_3m },
            { months: 6, price: match.sub_price_6m },
            { months: 12, price: match.sub_price_12m },
          ].filter((o) => o.price && o.price > 0);
          if (opts.length) {
            // Hvis ?binding=X i URL og prisen findes på bilen → forvalg den
            const preselect = opts.find((o) => o.months === initialBinding);
            setBindingMonths(preselect ? preselect.months : opts[opts.length - 1].months);
          }
        }
      })
      .catch(() => toast.error("Bilen kunne ikke indlæses"))
      .finally(() => setLoading(false));
  }, [plate]);

  const subOptions = useMemo(() => {
    if (!car) return [];
    return [
      { months: 1, price: car.sub_price_1m },
      { months: 3, price: car.sub_price_3m },
      { months: 6, price: car.sub_price_6m },
      { months: 12, price: car.sub_price_12m },
    ].filter((o) => o.price && o.price > 0);
  }, [car]);

  const selectedSub = subOptions.find((o) => o.months === bindingMonths);
  const subPrice = selectedSub?.price || 0;
  const total = subPrice + kmPackage.addon + insurance.addon;
  const endDate = addMonths(startDate, bindingMonths || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.phone) {
      toast.error("Udfyld navn, email og telefon");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/bookings`, {
        car_plate: car.license_plate,
        type: "rental",
        start_date: startDate,
        binding_months: bindingMonths,
        km_package: kmPackage.km,
        km_addon: kmPackage.addon,
        insurance_package: insurance.id,
        deductible: insurance.deductible,
        insurance_addon: insurance.addon,
        monthly_total: total,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        message: contact.message,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Booking fejlede — prøv igen");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F8F4ED] flex items-center justify-center text-graphite/60">Indlæser…</div>;
  if (!car) return (
    <div className="min-h-screen bg-[#F8F4ED] flex flex-col items-center justify-center gap-4 p-6">
      <div className="text-graphite/70">Bilen blev ikke fundet</div>
      <Link to="/biludlejning" className="btn-outline">← Tilbage</Link>
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8F4ED] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500" />
        <h2 className="font-heading text-3xl text-graphite">Tak — vi har modtaget din booking</h2>
        <p className="text-graphite/70 max-w-md">Vi vender tilbage indenfor 24 timer på {contact.email} eller {contact.phone}.</p>
        <Link to="/biludlejning" className="btn-outline mt-2">Tilbage til biludlejning</Link>
      </div>
    );
  }

  const heroImg = car.public_photos?.[0] ? cloudinaryThumb(car.public_photos[0]?.url || car.public_photos[0], "c_fill,w_1200,q_auto,f_auto") : null;

  return (
    <div className="min-h-screen bg-[#F8F4ED] py-8 pb-32 lg:pb-16" data-testid="book-car-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Link to={`/biludlejning/${plate}`} className="inline-flex items-center gap-1.5 text-graphite/60 hover:text-graphite text-sm mb-6" data-testid="book-back">
          <ChevronLeft size={16}/> Tilbage til bil
        </Link>

        {/* Steps */}
        <div className="flex flex-wrap items-center gap-6 mb-6" data-testid="book-steps">
          <Step n={1} label="Tilvalg" active />
          <Step n={2} label="Information på chauffør" />
          <Step n={3} label="Vælg betaling" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
          {/* LEFT: Form */}
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5DED1]">
            <h1 className="font-heading text-3xl text-graphite mb-6">Tilvalg</h1>

            {/* Startdato */}
            <Section title="Startdato">
              <label className="block group">
                <div className="flex items-center gap-3 border border-[#D7CFC0] rounded-xl px-4 py-3 bg-white hover:border-copper focus-within:border-copper focus-within:ring-2 focus-within:ring-copper/15 transition-all cursor-pointer">
                  <Calendar className="w-4 h-4 text-graphite/55" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-graphite/55">Startdato</div>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="book-date-input w-full bg-transparent outline-none text-graphite font-medium"
                      data-testid="book-start-date"
                    />
                  </div>
                </div>
              </label>
            </Section>

            {/* Abonnementsperiode */}
            <Section title="Abonnementsperiode" subtitle="Vælg din foretrukne abonnementsperiode.">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="book-binding">
                {subOptions.map((o) => {
                  const active = bindingMonths === o.months;
                  return (
                    <button
                      key={o.months}
                      type="button"
                      onClick={() => setBindingMonths(o.months)}
                      className={`rounded-xl border-2 px-4 py-3 text-center transition-all ${active ? "border-graphite bg-white" : "border-[#D7CFC0] hover:border-graphite/60"}`}
                      data-testid={`book-binding-${o.months}`}
                    >
                      <div className="text-graphite font-medium">{o.months === 1 ? "1 måned" : `${o.months} måneder`}</div>
                      <div className="text-graphite font-heading text-lg">{dkk(o.price)}</div>
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* Antal kilometer pr. måned */}
            <Section title="Antal kilometer pr. måned" subtitle="Har du brug for flere kilometer? Vælg en større kilometerpakke.">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="book-km-grid">
                {KM_PACKAGES.map((k) => {
                  const active = kmPackage.km === k.km;
                  return (
                    <button
                      key={k.km}
                      type="button"
                      onClick={() => setKmPackage(k)}
                      className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${active ? "border-graphite bg-white" : "border-[#D7CFC0] hover:border-graphite/60"}`}
                      data-testid={`book-km-${k.km}`}
                    >
                      <div className="text-graphite font-medium">{k.label}</div>
                      <div className={`text-sm ${k.addon === 0 ? "text-emerald-600 font-semibold" : "text-graphite"}`}>{k.note}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-graphite/55 mt-3 leading-relaxed">
                Hvis du under din abonnementsperiode kører mere end <strong>{(kmPackage.km * (bindingMonths || 0)).toLocaleString("da-DK")} km</strong>, vil du blive opkrævet <strong>{(car.excess_km_price || 2).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.</strong> pr. overkørt kilometer ved returnering af køretøjet.
              </p>
            </Section>

            {/* Selvrisiko-reduktion */}
            <Section title="Selvrisiko" subtitle="Forsikring og vejhjælp er inkluderet i prisen. Du kan reducere din selvrisiko ved at tilkøbe en pakke herunder.">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-start gap-2 text-sm" data-testid="book-included-banner">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-graphite">
                  <strong>Inkluderet i prisen:</strong> Ansvars- og kaskoforsikring (Danmark) · Vejhjælp 24/7 · Service og vedligehold
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2" data-testid="book-insurance-grid">
                {INSURANCE_PACKAGES.map((p) => {
                  const active = insurance.id === p.id;
                  const badgeColor = p.badge?.color === "emerald" ? "bg-emerald-500" : p.badge?.color === "blue" ? "bg-blue-700" : "";
                  const borderColor = active
                    ? p.badge?.color === "emerald" ? "border-emerald-500" : p.badge?.color === "blue" ? "border-blue-700" : "border-graphite"
                    : "border-[#D7CFC0]";
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setInsurance(p)}
                      className={`relative rounded-xl border-2 ${borderColor} bg-white p-4 pt-6 text-center transition-all hover:shadow-md`}
                      data-testid={`book-insurance-${p.id}`}
                    >
                      {p.badge && (
                        <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 ${badgeColor} text-white text-[10px] uppercase tracking-wider font-bold px-3 py-0.5 rounded`}>
                          {p.badge.text}
                        </span>
                      )}
                      <div className={`w-5 h-5 rounded-full border-2 mx-auto mb-3 flex items-center justify-center ${active ? (p.badge?.color === "emerald" ? "border-emerald-500" : p.badge?.color === "blue" ? "border-blue-700" : "border-graphite") : "border-[#D7CFC0]"}`}>
                        {active && <div className={`w-2.5 h-2.5 rounded-full ${p.badge?.color === "emerald" ? "bg-emerald-500" : p.badge?.color === "blue" ? "bg-blue-700" : "bg-graphite"}`} />}
                      </div>
                      <div className="font-bold text-graphite leading-tight mb-3">{p.title}</div>
                      <div className="text-sm text-graphite/80">
                        <div><strong>Selvrisiko:</strong></div>
                        <div className="mb-3">{dkk(p.deductible)}</div>
                      </div>
                      <div className={`font-semibold ${p.addon === 0 ? "text-graphite/60" : p.badge?.color === "emerald" ? "text-emerald-600" : "text-blue-700"}`}>
                        {p.addonLabel}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* Kontaktoplysninger */}
            <Section title="Dine kontaktoplysninger">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="book-contact-grid">
                <ContactField
                  icon={User}
                  label="Fulde navn"
                  value={contact.name}
                  onChange={(v) => setContact({ ...contact, name: v })}
                  testid="book-name"
                />
                <ContactField
                  icon={Phone}
                  label="Telefon"
                  type="tel"
                  value={contact.phone}
                  onChange={(v) => setContact({ ...contact, phone: v })}
                  testid="book-phone"
                />
                <ContactField
                  icon={Mail}
                  label="Email"
                  type="email"
                  value={contact.email}
                  onChange={(v) => setContact({ ...contact, email: v })}
                  testid="book-email"
                  span2
                />
                <ContactField
                  icon={MessageSquare}
                  label="Besked (valgfrit)"
                  textarea
                  value={contact.message}
                  onChange={(v) => setContact({ ...contact, message: v })}
                  testid="book-message"
                  span2
                />
              </div>
            </Section>

            <button type="submit" disabled={submitting || !bindingMonths} className="btn-primary w-full mt-2" data-testid="book-submit">
              {submitting ? "Sender…" : "Send booking-forespørgsel"}
            </button>
          </form>

          {/* RIGHT: Sticky summary (desktop only) */}
          <aside className="hidden lg:block lg:sticky lg:top-6 self-start">
            <SummaryCard
              car={car}
              heroImg={heroImg}
              startDate={startDate}
              endDate={endDate}
              bindingMonths={bindingMonths}
              siteInfo={siteInfo}
              kmPackage={kmPackage}
              insurance={insurance}
              subPrice={subPrice}
              total={total}
              testid="book-summary"
            />
          </aside>
        </div>
      </div>

      {/* MOBILE: Sticky bottom bar + drawer */}
      <div className="lg:hidden">
        {/* Drawer overlay */}
        <div
          onClick={() => setMobileSummaryOpen(false)}
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${mobileSummaryOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          data-testid="book-mobile-overlay"
        />
        {/* Drawer panel */}
        <div
          className={`fixed left-0 right-0 bottom-0 z-50 bg-[#F8F4ED] rounded-t-3xl shadow-2xl transition-transform duration-300 max-h-[85vh] overflow-y-auto ${mobileSummaryOpen ? "translate-y-0" : "translate-y-full"}`}
          data-testid="book-mobile-drawer"
        >
          <div className="sticky top-0 bg-[#F8F4ED] z-10 flex items-center justify-between px-5 pt-4 pb-3 border-b border-[#E5DED1]">
            <div className="w-10 h-1 bg-graphite/20 rounded-full absolute left-1/2 -translate-x-1/2 top-2" />
            <h3 className="font-heading text-xl text-graphite">Opsummering</h3>
            <button
              type="button"
              onClick={() => setMobileSummaryOpen(false)}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/5 text-graphite"
              data-testid="book-mobile-drawer-close"
              aria-label="Luk opsummering"
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-4">
            <SummaryCard
              car={car}
              heroImg={heroImg}
              startDate={startDate}
              endDate={endDate}
              bindingMonths={bindingMonths}
              siteInfo={siteInfo}
              kmPackage={kmPackage}
              insurance={insurance}
              subPrice={subPrice}
              total={total}
              testid="book-mobile-summary"
            />
          </div>
        </div>

        {/* Sticky bottom bar */}
        <div className="fixed left-0 right-0 bottom-0 z-30 bg-white/95 backdrop-blur-md border-t border-[#E5DED1] px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" data-testid="book-mobile-bar">
          <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-graphite/55">Total pr. måned</div>
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-xl text-graphite">{dkk(total)}</span>
                {bindingMonths && (
                  <span className="text-[11px] text-graphite/55 truncate">/ {bindingMonths} {bindingMonths === 1 ? "md." : "mdr."}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMobileSummaryOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-graphite/80 text-graphite text-sm font-medium hover:bg-graphite hover:text-white transition-colors"
              data-testid="book-mobile-show-summary"
            >
              Vis opsummering <ChevronUp size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, label, active }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "text-graphite" : "text-graphite/40"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-graphite text-white" : "border border-graphite/30"}`}>{n}</div>
      <span className={active ? "font-semibold" : ""}>{label}</span>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="mb-7 pb-7 border-b border-[#E5DED1] last:border-0 last:pb-0 last:mb-0">
      <h2 className="font-heading text-xl text-graphite mb-1">{title}</h2>
      {subtitle && <p className="text-sm text-graphite/65 mb-4">{subtitle}</p>}
      <div className={subtitle ? "" : "mt-3"}>{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-graphite/75">{label}</span>
      <span className="text-graphite font-medium tabular-nums">{value}</span>
    </div>
  );
}

function ContactField({ icon: Icon, label, value, onChange, type = "text", textarea = false, span2 = false, testid }) {
  return (
    <label className={`group relative flex items-start gap-3 bg-white border border-[#D7CFC0] rounded-xl px-4 py-3 hover:border-copper focus-within:border-copper focus-within:ring-2 focus-within:ring-copper/15 transition-all cursor-text ${span2 ? "sm:col-span-2" : ""}`}>
      <Icon className="w-4 h-4 text-graphite/55 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-graphite/55">{label}</div>
        {textarea ? (
          <textarea
            rows="2"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-transparent outline-none text-graphite font-medium resize-none placeholder:text-graphite/30"
            data-testid={testid}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-transparent outline-none text-graphite font-medium placeholder:text-graphite/30"
            data-testid={testid}
          />
        )}
      </div>
    </label>
  );
}

function SummaryCard({ car, heroImg, startDate, endDate, bindingMonths, siteInfo, kmPackage, insurance, subPrice, total, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5DED1] overflow-hidden" data-testid={testid}>
      {heroImg ? (
        <img src={heroImg} alt={`${car.make} ${car.model}`} className="w-full aspect-[16/10] object-cover" />
      ) : (
        <div className="w-full aspect-[16/10] bg-[#F1EBE1] flex items-center justify-center">
          <div className="text-center px-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-graphite/40 mb-1">{car.make} {car.model}</div>
            <div className="font-heading text-lg text-graphite/65">Billeder på vej</div>
          </div>
        </div>
      )}
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-wider text-graphite/55 mb-1">{car.category || "BIL"}</div>
        <h2 className="font-heading text-2xl text-graphite mb-4">{car.make} {car.model}</h2>

        <div className="space-y-2 text-sm mb-4">
          <div className="flex items-start gap-2 text-graphite/80">
            <Calendar className="w-4 h-4 text-graphite/55 mt-0.5 shrink-0"/>
            <div>
              <div>Fra <strong>{fmtDate(startDate)}</strong></div>
              <div>til <strong>{fmtDate(endDate)}</strong></div>
              <div className="text-graphite/55">{bindingMonths || "—"} måneders abonnement</div>
            </div>
          </div>
          {siteInfo.address && (
            <div className="flex items-start gap-2 text-graphite/80">
              <MapPin className="w-4 h-4 text-graphite/55 mt-0.5 shrink-0"/>
              <div>
                <div><strong>{siteInfo.company_name || "Mahasa"}</strong></div>
                <div className="text-graphite/60">{siteInfo.address}</div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-[#E5DED1] pt-4">
          <h3 className="font-heading text-lg text-graphite mb-3">Prisoversigt</h3>
          <div className="space-y-1.5 text-sm">
            <Row label="Abonnementspris" value={dkk(subPrice)} />
            <Row label={`${kmPackage.km} km/måned`} value={kmPackage.addon === 0 ? "Inkluderet" : dkk(kmPackage.addon)} />
            {insurance.addon > 0 ? (
              <Row label={insurance.title} value={dkk(insurance.addon)} />
            ) : (
              <Row label="Selvrisiko (standard)" value="Inkluderet" />
            )}
            <Row label="Forsikring + vejhjælp" value="Inkluderet" />
          </div>
          <div className="flex items-baseline justify-between pt-3 mt-3 border-t border-[#E5DED1]">
            <span className="font-heading text-graphite">Total pr. måned (DKK)</span>
            <span className="font-heading text-2xl text-graphite">{dkk(total)}</span>
          </div>
          {bindingMonths && (
            <p className="text-xs text-graphite/60 mt-2 text-right" data-testid="book-period-total">
              Samlet pris over {bindingMonths} {bindingMonths === 1 ? "måned" : "måneder"}:{" "}
              <strong className="text-graphite">{dkk(total * bindingMonths)}</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
